/**
 * Branding Context
 *
 * Provides tenant branding information throughout the application.
 * Fetches branding from /api/branding based on current subdomain.
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { hexToRgba, lightenColor, darkenColor } from '@/utils/color';

export interface TenantBranding {
  type: 'customer' | 'service_integrator' | null;
  slug: string | null;
  name: string;
  logoUrl?: string;
  brandName?: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  showLogoOnly?: boolean;
  theme?: {
    name: string;
    displayName: string;
    config: any;
  } | null;
  features: {
    ssoEnabled: boolean;
    whiteLabelEnabled: boolean;
    customDomain?: string;
  };
}

interface BrandingContextType {
  branding: TenantBranding;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// Serac ice palette — hex approximations of OKLCH tokens so legacy
// hexToRgba/lightenColor/darkenColor utilities keep working.
//   primary  = ice-500  ≈ oklch(0.66 0.150 205)
//   secondary = ice-700 ≈ oklch(0.48 0.130 205)
//   accent   = signal-ok ≈ oklch(0.76 0.130 165)
// TODO(serac-rebrand 2026-04): once all consumers read from
// var(--ice-500) directly, drop these hex defaults and the
// dynamic per-tenant theming below.
const defaultBranding: TenantBranding = {
  type: null,
  slug: null,
  name: 'Serac',
  brandName: 'Serac',
  primaryColor: '#1FA8D9',
  secondaryColor: '#0F6F95',
  accentColor: '#2EC9A6',
  showLogoOnly: false,
  theme: null,
  features: {
    ssoEnabled: false,
    whiteLabelEnabled: false,
  }
};

const BrandingContext = createContext<BrandingContextType>({
  branding: defaultBranding,
  isLoading: true,
  error: null,
  refetch: async () => {},
});

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<TenantBranding>(defaultBranding);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBranding = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Auth is provided by the httpOnly cookie attached automatically when
      // `credentials: 'include'`. The /api/branding endpoint reads cookies →
      // Bearer (CLI) → public default in that order, so unauthenticated visitors
      // still get tenant branding by subdomain.
      const response = await fetch('/api/branding', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch branding');
      }

      const data = await response.json();
      if (data.success && data.branding) {
        setBranding(data.branding);

        // Apply CSS custom properties for dynamic theming
        applyBrandingStyles(data.branding);
      }
    } catch (err) {
      console.error('Error fetching branding:', err);
      setError(err instanceof Error ? err.message : 'Failed to load branding');
      // Keep default branding on error
    } finally {
      setIsLoading(false);
    }
  };

  // Track last fetch time to prevent rate limiting
  const lastFetchRef = React.useRef<number>(0);
  const fetchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const debouncedFetchBranding = React.useCallback(() => {
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchRef.current;

    // Prevent fetching more than once per 2 seconds
    if (timeSinceLastFetch < 2000) {
      // Clear any existing timeout
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      // Schedule a fetch after the cooldown
      fetchTimeoutRef.current = setTimeout(() => {
        lastFetchRef.current = Date.now();
        fetchBranding();
      }, 2000 - timeSinceLastFetch);
      return;
    }

    lastFetchRef.current = now;
    fetchBranding();
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchBranding();
  }, []);

  // Re-apply brand tints when theme changes (light ↔ dark)
  useEffect(() => {
    const observer = new MutationObserver(() => {
      applyBrandingStyles(branding);
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, [branding]);

  // Refetch when auth changes (login/logout).
  //
  // We can't observe cookie changes from JS (httpOnly), so the auth flow
  // dispatches synthetic events:
  //   - `customer-token-changed` after a customer login (in-memory token set)
  //   - `auth-cleared` after `baseClient.clearAuth()` runs on logout
  // Cross-tab logout is detected via the auth-cleared event on the
  // originating tab; for stricter cross-tab sync, the next API call in the
  // other tab will 401 (cookie cleared) and AuthContext will redirect.
  useEffect(() => {
    const handleTokenChange = () => {
      debouncedFetchBranding();
    };

    const handleAuthCleared = () => {
      // Reset to default branding immediately, then refetch
      setBranding(defaultBranding);
      debouncedFetchBranding();
    };

    window.addEventListener('customer-token-changed', handleTokenChange);
    window.addEventListener('auth-cleared', handleAuthCleared);

    return () => {
      window.removeEventListener('customer-token-changed', handleTokenChange);
      window.removeEventListener('auth-cleared', handleAuthCleared);
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [debouncedFetchBranding]);

  return (
    <BrandingContext.Provider value={{ branding, isLoading, error, refetch: fetchBranding }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
}

/**
 * Apply branding colors as CSS custom properties
 */
function applyBrandingStyles(branding: TenantBranding) {
  const root = document.documentElement;
  const isDark = root.getAttribute('data-theme') !== 'light';

  // Set CSS custom properties
  root.style.setProperty('--brand-primary', branding.primaryColor);
  root.style.setProperty('--brand-secondary', branding.secondaryColor);
  root.style.setProperty('--brand-accent', branding.accentColor);

  // Generate theme-aware variants
  if (isDark) {
    // Dark: semi-transparent tints
    root.style.setProperty('--brand-primary-light', hexToRgba(branding.primaryColor, 0.15));
    root.style.setProperty('--brand-secondary-light', hexToRgba(branding.secondaryColor, 0.15));
    root.style.setProperty('--brand-accent-light', hexToRgba(branding.accentColor, 0.15));
  } else {
    // Light: solid pastel tints
    root.style.setProperty('--brand-primary-light', lightenColor(branding.primaryColor, 85));
    root.style.setProperty('--brand-secondary-light', lightenColor(branding.secondaryColor, 85));
    root.style.setProperty('--brand-accent-light', lightenColor(branding.accentColor, 85));
  }
  root.style.setProperty('--brand-primary-dark', darkenColor(branding.primaryColor, 20));

  // Update meta theme-color for mobile browsers
  const metaTheme = document.querySelector('meta[name="theme-color"]');
  if (metaTheme) {
    metaTheme.setAttribute('content', branding.primaryColor);
  }
}

export default BrandingContext;
